// cors_iframe.js
import { append_organization_id_query_param_to_url } from '../helpers/action_request_helpers';
import {
	normalize_data_hub_privacy_policy_string,
} from '../helpers/data_hub_privacy_policy';
import {
	necessary_browser_storage_user_data_fields,
	write_organization_jwt_cache_entry,
} from '../helpers/organization_jwt_local_storage_cache';
import { white_list_outgoing_person_fields } from '../helpers/static_vars';
import { merge_user_data_browser } from '../helpers/user_data_smart_merge';

/**
 * Builds the common payload metadata sent to the CORS iframe with every message.
 * @param {Object} data_hub_instance
 * @returns {Object}
 */
function build_cors_iframe_message_payload_base(data_hub_instance) {
	return {
		privacy_policy: normalize_data_hub_privacy_policy_string(data_hub_instance?.privacy_policy),
		organization_id: data_hub_instance?.organization_id || null,
	};
}

/**
 * Returns the deployed DataHub JS version string for the iframe URL cache key.
 * The value is optional: older bundles that do not expose a version should keep
 * the previous URL shape instead of sending an empty or misleading query param.
 * @param {Object} data_hub_instance
 * @returns {string|null}
 */
function get_data_hub_version_for_cors_iframe_cache_key(data_hub_instance) {
	const version_candidates = [
		data_hub_instance?.version,
		typeof window !== 'undefined' ? window?.DataHub?.version : null,
	];

	let raw_data_hub_version = null;
	for (const version_candidate of version_candidates) {
		if (version_candidate === null || version_candidate === undefined) {
			continue;
		}
		const candidate_string = String(version_candidate).trim();
		if (candidate_string.length === 0) {
			continue;
		}
		raw_data_hub_version = candidate_string;
		break;
	}

	if (!raw_data_hub_version) {
		return null;
	}

	let normalized_data_hub_version = raw_data_hub_version;
	if (normalized_data_hub_version.startsWith('v') || normalized_data_hub_version.startsWith('V')) {
		normalized_data_hub_version = normalized_data_hub_version.slice(1).trim();
	}
	if (normalized_data_hub_version.startsWith('.')) {
		normalized_data_hub_version = `0${normalized_data_hub_version}`;
	}

	normalized_data_hub_version = normalized_data_hub_version.replace(/[^A-Za-z0-9._-]/g, '_');
	if (normalized_data_hub_version.length === 0) {
		return null;
	}
	return normalized_data_hub_version;
}

/**
 * Builds the cached CORS iframe URL without a random cache-buster.
 * @param {string} forms_domain
 * @param {Object} data_hub_instance
 * @returns {string}
 */
function build_data_hub_cors_iframe_src_url(forms_domain, data_hub_instance) {
	const parentDomain = window.location.origin;
	let iframe_src = `${forms_domain}/cors_iframe?parent_domain=${encodeURIComponent(parentDomain)}`;
	iframe_src = append_organization_id_query_param_to_url(iframe_src, data_hub_instance?.organization_id);
	const data_hub_version_for_cache_key = get_data_hub_version_for_cors_iframe_cache_key(data_hub_instance);
	if (data_hub_version_for_cache_key) {
		try {
			const parsed_iframe_src = new URL(iframe_src);
			parsed_iframe_src.searchParams.set('data_hub_version', data_hub_version_for_cache_key);
			iframe_src = parsed_iframe_src.toString();
		} catch {
			// Keep the iframe usable even if an older caller provided a non-absolute forms_domain.
		}
	}
	return iframe_src;
}

/**
 * Posts a DATA_HUB_* message to the hidden CORS iframe after the iframe has loaded.
 * Multiple callers can queue messages against the same iframe during one submit flow,
 * which lets the JWT write and wide user_data cache write land on the forms domain
 * without racing the iframe's inline message listener setup.
 *
 * @param {string} forms_domain
 * @param {Object} data_hub_instance
 * @param {Object} message
 * @returns {void}
 */
function post_message_to_data_hub_cors_iframe(forms_domain, data_hub_instance, message) {
	if (forms_domain === window.location.origin) {
		return;
	}

	let iframe = document.getElementById('dataHubIframe');
	const iframe_was_created = !iframe;
	if (!iframe) {
		iframe = document.createElement('iframe');
		iframe.id = 'dataHubIframe';
		iframe.style.display = 'none';
		iframe.sandbox = "allow-scripts allow-same-origin allow-forms allow-storage-access-by-user-activation";
	}

	const send_message_to_iframe = () => {
		iframe.contentWindow.postMessage(message, forms_domain);
	};

	if (iframe.dataset.dataHubIframeLoaded === 'true') {
		send_message_to_iframe();
		return;
	}

	// Queue this specific message until the iframe is ready. Each caller adds its
	// own one-time listener so a submit can send both the JWT and the full user_data
	// without one message replacing the other.
	iframe.addEventListener('load', () => {
		iframe.dataset.dataHubIframeLoaded = 'true';
		send_message_to_iframe();
	}, { once: true });

	if (iframe_was_created) {
		iframe.src = build_data_hub_cors_iframe_src_url(forms_domain, data_hub_instance);
		document.body.appendChild(iframe);
	}
}

/**
 * Copies only outgoing person fields from a CORS iframe payload candidate.
 * The parent window should never treat the full postMessage envelope as person data;
 * this keeps localStorage metadata, JWT strings, and transport flags out of user_data.
 *
 * @param {Object|null|undefined} user_data_candidate
 * @returns {Object}
 */
function filter_cors_user_data_candidate_to_outgoing_fields(user_data_candidate) {
	const filtered_user_data = {};
	if (
		!user_data_candidate ||
		typeof user_data_candidate !== 'object' ||
		Array.isArray(user_data_candidate)
	) {
		return filtered_user_data;
	}

	Object.keys(user_data_candidate).forEach((key) => {
		if (white_list_outgoing_person_fields.includes(key)) {
			filtered_user_data[key] = user_data_candidate[key];
		}
	});

	return filtered_user_data;
}

/**
 * Applies the browser storage privacy mode before sending user_data to the iframe.
 * `all` mirrors wide prefill data, `necessary` mirrors only the identity fields needed
 * to keep the org-scoped JWT/cache link alive, and `none` mirrors nothing.
 *
 * @param {Object} user_data
 * @param {Object} data_hub_instance
 * @returns {Object}
 */
function filter_user_data_for_cors_iframe_storage_policy(user_data, data_hub_instance) {
	const privacy_policy = normalize_data_hub_privacy_policy_string(data_hub_instance?.privacy_policy);
	if (privacy_policy === 'none') {
		return {};
	}
	if (privacy_policy !== 'necessary') {
		return user_data;
	}

	const necessary_user_data = {};
	Object.keys(user_data || {}).forEach((key) => {
		if (necessary_browser_storage_user_data_fields.includes(key)) {
			necessary_user_data[key] = user_data[key];
		}
	});
	return necessary_user_data;
}

/**
 * Merges all user_data sources returned by the CORS iframe into one parent payload.
 * The forms-domain localStorage cache is merged first, then fresh server/JWT data is
 * layered on top so new server fields win while older wide prefill fields survive.
 *
 * @param {Object} message_payload
 * @returns {Promise<Object>}
 */
async function merge_cors_iframe_user_data_payload(message_payload) {
	// Be tolerant of any envelope shape. Older forms-domain iframe builds only send
	// `user` / `user_data` and never the localStorage-mirror keys; newer builds add
	// `local_storage_user_data`. Missing or non-object candidates are simply skipped,
	// so this stays a no-op rather than throwing when a key the parent hopes for is absent.
	if (!message_payload || typeof message_payload !== 'object' || Array.isArray(message_payload)) {
		return {};
	}

	const user_data_candidates = [
		message_payload.local_storage_user_data || null,
		message_payload.stored_user_data || null,
		message_payload.cached_user_data || null,
		message_payload.user_data || null,
		message_payload.user || null,
	];

	let merged_user_data = {};
	for (const user_data_candidate of user_data_candidates) {
		const filtered_user_data = filter_cors_user_data_candidate_to_outgoing_fields(user_data_candidate);
		if (Object.keys(filtered_user_data).length === 0) {
			continue;
		}
		merged_user_data = await merge_user_data_browser(merged_user_data, filtered_user_data);
	}

	return merged_user_data;
}

/**
 * Send a jwt_cookie to the CORS iframe.
 * @param {String} forms_domain for data-hub-action backend
 * @param {String} jwt_cookie returns from new user data post
 * @param {String} event_uuid of the user data post event
 * @param {Object} data_hub_instance
 * @returns
 */
export const send_jwt_cookie_to_cors_iframe = async (forms_domain, jwt_cookie = null, event_uuid = null, data_hub_instance = window?.data_hub) => {
    const error_function = data_hub_instance?.error_function || console.error;
    if (normalize_data_hub_privacy_policy_string(data_hub_instance?.privacy_policy) === 'none') {
        return;
    }
    if (!forms_domain) {
        return error_function?.('No form domain provided for send_user_data_to_cors_iframe', null);
    }
    if (!jwt_cookie || typeof jwt_cookie !== 'string') {
        if (!data_hub_instance?.jwt_cookie || typeof data_hub_instance.jwt_cookie !== 'string') {
            return error_function?.('No valid jwt_cookie provided', null);
        } else {
            jwt_cookie = data_hub_instance.jwt_cookie;
        }
    }

    try {

        post_message_to_data_hub_cors_iframe(forms_domain, data_hub_instance, {
            type: 'DATA_HUB_SET_JWT_COOKIE',
            payload: {
                ...build_cors_iframe_message_payload_base(data_hub_instance),
                jwt_cookie: jwt_cookie,
                event_uuid: event_uuid
            }
        });
    } catch (error) {
        error_function?.('Error sending user data to CORS iframe: ' + error, null);
    }
};

/**
 * Sends the fullest browser user_data and the returned JWT to the forms-domain iframe.
 * The iframe stores user_data in its own localStorage slot and uses the JWT to set the
 * forms-domain HttpOnly cookie through the single /api/post/set_jwt_cookie endpoint.
 *
 * @param {String} forms_domain for action-two backend
 * @param {Object} user_data merged user_data to mirror on the forms-domain origin
 * @param {String|null} jwt_cookie JWT returned from the user_data POST
 * @param {String|null} event_uuid event_uuid returned from the user_data POST
 * @param {Object} data_hub_instance
 * @returns {Promise<void>}
 */
export const send_user_data_to_cors_iframe = async (
    forms_domain,
    user_data,
    jwt_cookie = null,
    event_uuid = null,
    data_hub_instance = window?.data_hub
) => {
    const error_function = data_hub_instance?.error_function || console.error;
    if (normalize_data_hub_privacy_policy_string(data_hub_instance?.privacy_policy) === 'none') {
        return;
    }
    if (!forms_domain) {
        return error_function?.('No form domain provided for send_user_data_to_cors_iframe', null);
    }
    if (
        (!user_data || typeof user_data !== 'object' || Object.keys(user_data).length === 0) &&
        (!jwt_cookie || typeof jwt_cookie !== 'string')
    ) {
        return error_function?.('No valid user_data or jwt_cookie provided', null);
    }

    try {

        let user_data_for_cors_storage = {};
        if (user_data && typeof user_data === 'object') {
            user_data_for_cors_storage = filter_user_data_for_cors_iframe_storage_policy(user_data, data_hub_instance);
        }
        if (Object.keys(user_data_for_cors_storage).length === 0 && (!jwt_cookie || typeof jwt_cookie !== 'string')) {
            return;
        }

        post_message_to_data_hub_cors_iframe(forms_domain, data_hub_instance, {
            type: 'DATA_HUB_SET_USER_DATA',
            payload: {
                ...build_cors_iframe_message_payload_base(data_hub_instance),
                user_data: user_data_for_cors_storage,
                jwt_cookie: jwt_cookie,
                event_uuid: event_uuid
            }
        });
    } catch (error) {
        error_function?.('Error sending user data to CORS iframe: ' + error, null);
    }
};

export const get_user_data_from_cors_iframe = (forms_domain, url_params = '', data_hub_instance) => {
    const error_function = data_hub_instance?.error_function || console.error;
    if (normalize_data_hub_privacy_policy_string(data_hub_instance?.privacy_policy) === 'none') {
        return;
    }
    try {
        if (forms_domain !== window.location.origin) {
            // Forward the parent's cached JWT so the iframe can send it as
            // Authorization: Bearer on its outgoing /api/get/user_jwt fetch.
            // In Safari ITP / partitioned third-party contexts the iframe's own
            // cookie + LS may be empty, so without this the cross-origin GET
            // downgrades to a fresh unknown_uuid every refresh cycle.
            const parent_cached_jwt = typeof data_hub_instance?.jwt_cookie === 'string'
                && data_hub_instance.jwt_cookie.length > 0
                    ? data_hub_instance.jwt_cookie
                    : null;
            const body_object_with_url_params = {
                ...build_cors_iframe_message_payload_base(data_hub_instance),
                url_params: url_params,
                parent_cached_jwt: parent_cached_jwt,
            };
            post_message_to_data_hub_cors_iframe(forms_domain, data_hub_instance, {
                type: 'DATA_HUB_GET_USER_DATA',
                payload: body_object_with_url_params
            });
        }
    } catch (error) {
        error_function?.('Error getting user data from CORS iframe: ' + error, null);
    }
};

/**
 * Send a clear JWT cookie request through the CORS iframe.
 * For cross-origin scenarios where the forms domain cookies need to be cleared
 * from the forms domain context (not the parent domain).
 * Fire-and-forget — does not return a promise or block.
 * @param {String} forms_domain for action-two backend
 * @param {Object} data_hub_instance
 */
export const send_clear_jwt_to_cors_iframe = (forms_domain, data_hub_instance = window?.data_hub) => {
    const error_function = data_hub_instance?.error_function || console.error;
    if (normalize_data_hub_privacy_policy_string(data_hub_instance?.privacy_policy) === 'none') {
        return;
    }
    if (!forms_domain) {
        return error_function?.('No form domain provided for send_clear_jwt_to_cors_iframe', null);
    }

    try {
        if (forms_domain !== window.location.origin) {
            post_message_to_data_hub_cors_iframe(forms_domain, data_hub_instance, {
                type: 'DATA_HUB_CLEAR_JWT_COOKIE',
                payload: build_cors_iframe_message_payload_base(data_hub_instance),
            });
        }
    } catch (error) {
        error_function?.('Error sending clear jwt to CORS iframe: ' + error, null);
    }
};

// The message listener is global. Keep the active instance mutable so repeated constructor
// calls do not stack duplicate listeners, while the listener still uses the newest instance
// when processing iframe responses.
let active_data_hub_message_listener_instance = null;
let data_hub_message_listener_bound = false;

/**
 * Attach a single message listener on window that handles all DATA_HUB_* messages.
 * This listener is attached immediately so that even if forms are added later,
 * incoming messages are processed.
 *
 * @param {Object|null} data_hub_instance
 * @returns {void}
 */
export function addDataHubMessageListener(data_hub_instance = null) {
    active_data_hub_message_listener_instance = data_hub_instance
        || active_data_hub_message_listener_instance
        || (typeof window !== 'undefined' ? window?.data_hub : null)
        || null;

    if (data_hub_message_listener_bound || typeof window === 'undefined') {
        return;
    }
    data_hub_message_listener_bound = true;

    window.addEventListener(
        'message',
        async (event) => {
            const data_hub_instance_for_message = active_data_hub_message_listener_instance
                || window?.data_hub
                || null;
            const raw_message_data = event?.data;

            // postMessage data can be any serializable value; only handle plain envelope objects.
            if (
                raw_message_data === null ||
                raw_message_data === undefined ||
                typeof raw_message_data !== 'object' ||
                Array.isArray(raw_message_data)
            ) {
                return;
            }

            const message_type = raw_message_data.type;
            if (typeof message_type !== 'string' || !message_type.startsWith('DATA_HUB_')) {
                return;
            }

            const raw_payload = raw_message_data.payload;
            const message_payload =
                raw_payload !== null &&
                raw_payload !== undefined &&
                typeof raw_payload === 'object' &&
                !Array.isArray(raw_payload)
                    ? raw_payload
                    : {};

            // Use the forms_domain from DataHub if available, or fallback to window.location.origin.
            const forms_domain = data_hub_instance_for_message?.forms_domain
                || (window?.data_hub && window?.data_hub?.forms_domain)
                || window.location.origin;

            // Only process events from the expected origin.
            if (event.origin !== forms_domain) {
                return;
            }

            const assign_jwt_cookie_from_message_payload_if_valid_string = () => {
                const jwt_cookie_value = message_payload.jwt_cookie;
                if (
                    typeof jwt_cookie_value === 'string' &&
                    jwt_cookie_value.length > 0 &&
                    data_hub_instance_for_message
                ) {
                    data_hub_instance_for_message.jwt_cookie = jwt_cookie_value;
                }
            };

            // Process the different DATA_HUB message types.
            switch (message_type) {
                // Pass in jwt with event_uuid to verify pre signed with 5 second csfr expiry cookie
                case 'DATA_HUB_SET_JWT_COOKIE_SUCCESS':
                    assign_jwt_cookie_from_message_payload_if_valid_string();
                    break;
                case 'DATA_HUB_SET_JWT_COOKIE_ERROR':
                    // 251111 mobile safari is blocking. so just dont error so it doesnt give weird ux
                    // data_hub_instance?.error_function?.('Error setting jwt_cookie in CORS iframe: ' + event?.data)
                    // || console.error('Error setting jwt_cookie in CORS iframe: ' + event?.data);
                    break;

                // Older pass in raw user_data
                case 'DATA_HUB_COOKIE_SET_SUCCESS':
                    assign_jwt_cookie_from_message_payload_if_valid_string();
                    break;
                case 'DATA_HUB_COOKIE_SET_ERROR':
                    // Same
                    // data_hub_instance?.error_function?.('Error setting user_data cookie in CORS iframe: ' + event?.data)
                    // || console.error('Error setting user_data cookie in CORS iframe: ' + event?.data);
                    break;

                // GET cached user data via jwt_cookie
                case 'DATA_HUB_COOKIE_GET_SUCCESS': {
                    // Only accept explicit user objects. The prior `|| message_payload` fallback
                    // would treat the whole envelope (jwt_cookie, privacy_policy, etc.) as a
                    // person payload — today the whitelist drops those, but we shouldn't rely
                    // on that for safety.
                    assign_jwt_cookie_from_message_payload_if_valid_string();
                    const dh = data_hub_instance_for_message || window?.data_hub;
                    const white_listed_user_data = await merge_cors_iframe_user_data_payload(message_payload);

                    // Skip the merge + re-prefill + event dispatch when the server returned
                    // nothing usable. The merge is a no-op for {} anyway, but firing
                    // data-hub-new-user-data and re-prefilling with unchanged data is noise.
                    // Still finish the async user-data load and persist a returned JWT; otherwise
                    // a CORS response that only carries jwt_cookie can leave init waiters hanging
                    // and drop the cache update that future analytics / prefill paths rely on.
                    if (Object.keys(white_listed_user_data).length === 0) {
                        if (dh?.jwt_cookie) {
                            write_organization_jwt_cache_entry(dh, dh.jwt_cookie);
                        }
                        if (typeof dh?.resolve_user_data_loaded === 'function') {
                            dh.resolve_user_data_loaded();
                        }
                        break;
                    }

                    if (dh && typeof dh.got_cors_user_data_cache === 'function') {
                        dh.got_cors_user_data_cache(white_listed_user_data);
                    }

                    break;
                }
                case 'DATA_HUB_COOKIE_GET_ERROR':
                    // same
                    // data_hub_instance?.error_function?.('Error getting user_data cookie from CORS iframe: ' + event?.data)
                    // || console.error('Error getting user_data cookie from CORS iframe: ' + event?.data);
                    break;

                case 'DATA_HUB_CLEAR_JWT_COOKIE_SUCCESS': {
                    // Follow same pattern as DATA_HUB_COOKIE_GET_SUCCESS → got_cors_user_data_cache
                    // Delegate to data_hub_instance method for re_prefill, events, etc.
                    const dh_clear = data_hub_instance_for_message || window?.data_hub;
                    if (dh_clear && typeof dh_clear.got_clear_jwt_response === 'function') {
                        dh_clear.got_clear_jwt_response(message_payload);
                    }
                    break;
                }
                case 'DATA_HUB_CLEAR_JWT_COOKIE_ERROR':
                    // Suppress errors like other iframe operations — don't break UX
                    break;

                default:
                    // Unknown message type—do nothing.
                    break;
            }
        },
        false
    );
}
